import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { brandsService } from "api/services/brands.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { getApiErrorMessage } from "utils/api-error";
import { formatBrandDate } from "utils/constant/brand";

import type { AdminBrand } from "api/types/brand.types";
import "pages/AdminPage.scss";
import "./BrandDetail.scss";

const BrandDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [brand, setBrand] = useState<AdminBrand | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // 라우트가 /brand-list/:id 라 비정수 id 도 도달한다. 서버에 보내지 않고 바로 막는다.
    const brandId = Number(id);
    if (!Number.isInteger(brandId) || brandId <= 0) {
      setBrand(null);
      setError("요청한 데이터를 찾을 수 없습니다.");
      return;
    }

    // id 가 연속으로 바뀌면 늦게 도착한 이전 응답이 화면을 덮어쓰지 않도록 무시한다.
    let cancelled = false;
    setLoading(true);
    setError("");
    brandsService
      .adminGet(brandId)
      .then((res) => {
        if (!cancelled) setBrand(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setBrand(null);
        setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const goList = () => navigate("/brand-list");

  return (
    <div className="admin-page">
      <ContentHeader
        title="브랜드 상세"
        description={brand?.name}
        actions={[
          <Button key="back" variant="secondary" onClick={goList}>
            목록으로
          </Button>,
        ]}
      />

      <div className="admin-page__card brand-detail">
        {loading ? (
          <div className="brand-detail__loading">불러오는 중...</div>
        ) : error ? (
          <div className="admin-page__error">{error}</div>
        ) : (
          brand && (
            <div className="admin-page__section">
              <div className="admin-page__section-title">기본 정보</div>
              <dl className="brand-detail__grid">
                <DetailRow label="ID">{brand.id}</DetailRow>
                <DetailRow label="브랜드명">
                  <span className="brand-detail__name">
                    {brand.name}
                    {brand.is_default && <Badge variant="info">기본</Badge>}
                  </span>
                </DetailRow>
                <DetailRow label="상태">{brand.is_active ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>}</DetailRow>
                <DetailRow label="파트너 URL">
                  <a className="brand-detail__link" href={brand.partner_url} target="_blank" rel="noreferrer">
                    {brand.partner_url}
                  </a>
                </DetailRow>
                <DetailRow label="설명">
                  <span className="brand-detail__desc">{brand.description || "-"}</span>
                </DetailRow>
                <DetailRow label="생성일">{formatBrandDate(brand.created_at)}</DetailRow>
              </dl>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="brand-detail__row">
    <dt className="brand-detail__label">{label}</dt>
    <dd className="brand-detail__value">{children}</dd>
  </div>
);

export default BrandDetail;
